import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface Props {
  password: string;
}

type Strength = 1 | 2 | 3 | 4 | 5;

interface StrengthInfo {
  level: Strength;
  label: string;
  barColor: string;
  textColor: string;
}

interface RequirementCheck {
  met: boolean;
  label: string;
}

interface PasswordRequirements {
  length8: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

function checkRequirements(password: string): PasswordRequirements {
  return {
    length8: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password),
  };
}

function calcStrength(password: string): Strength {
  if (!password) return 1;

  const requirements = checkRequirements(password);
  const metCount = Object.values(requirements).filter(Boolean).length;

  // 길이 보너스
  const lengthBonus =
    password.length >= 16 ? 2 : password.length >= 12 ? 1 : 0;

  const totalScore = metCount + lengthBonus;

  if (totalScore <= 1) return 1;
  if (totalScore <= 2) return 2;
  if (totalScore <= 3) return 3;
  if (totalScore <= 4) return 4;
  return 5;
}

const STRENGTH_INFO: Record<Strength, StrengthInfo> = {
  1: { level: 1, label: '매우 약함', barColor: 'bg-red-500', textColor: 'text-red-600' },
  2: { level: 2, label: '약함', barColor: 'bg-orange-500', textColor: 'text-orange-600' },
  3: { level: 3, label: '보통', barColor: 'bg-yellow-500', textColor: 'text-yellow-600' },
  4: { level: 4, label: '강함', barColor: 'bg-green-500', textColor: 'text-green-600' },
  5: { level: 5, label: '매우 강함', barColor: 'bg-emerald-600', textColor: 'text-emerald-700' },
};

const PasswordStrengthMeter: React.FC<Props> = ({ password }) => {
  const strength = useMemo(() => calcStrength(password), [password]);
  const info = useMemo(() => STRENGTH_INFO[strength], [strength]);
  const requirements = useMemo(() => checkRequirements(password), [password]);

  if (!password) return null;

  const requirementsList: RequirementCheck[] = [
    { met: requirements.length8, label: '8자 이상' },
    { met: requirements.hasUppercase, label: '대문자 포함' },
    { met: requirements.hasLowercase, label: '소문자 포함' },
    { met: requirements.hasNumber, label: '숫자 포함' },
    { met: requirements.hasSpecial, label: '특수문자 포함' },
  ];

  const filledBars = Math.min(strength, 4);

  return (
    <div
      className="mt-4 space-y-3"
      aria-live="polite"
      aria-atomic="true"
      role="region"
      aria-label="비밀번호 강도 표시기"
    >
      {/* 강도 라벨 */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          비밀번호 강도
        </span>
        <span
          className={`text-sm font-semibold ${info.textColor}`}
          aria-label={`비밀번호 강도: ${info.label}`}
        >
          {info.label}
        </span>
      </div>

      {/* 프로그레스 바 (4단계) */}
      <div
        className="flex gap-1"
        role="progressbar"
        aria-valuenow={filledBars}
        aria-valuemin={0}
        aria-valuemax={4}
        aria-label={`강도 진행도: ${filledBars}/${4}`}
      >
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded transition-colors duration-300 ${
              i <= filledBars ? info.barColor : 'bg-gray-200 dark:bg-gray-700'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* 요구사항 체크리스트 */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
          요구사항:
        </p>
        <ul className="space-y-1.5">
          {requirementsList.map((req, idx) => (
            <li key={idx} className="flex items-center gap-2">
              {req.met ? (
                <Check
                  size={16}
                  className="text-green-600 dark:text-green-500 flex-shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <X
                  size={16}
                  className="text-gray-400 dark:text-gray-600 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
              <span
                className={`text-xs ${
                  req.met
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-500'
                }`}
              >
                {req.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
